import { ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'
import { IOrganizationChangeResponse } from '@v2/domains/organization/organization.types'
import { setUserSession } from '@v2/domains/user/store/user.store'
import { IUserSessionStore } from '@v2/domains/user/user.types'
import { rootApi } from '@v2/store/api/rootApi'

import {
  AuthTokenDto,
  OrganizationDTOV2,
  RefreshTokenDtoV3,
  SignInDtoV3,
  UserEmailDto,
} from 'src/api/generated'
import { FUTURE_BANK } from 'src/default/config'

import { setAuthInfo } from '../store/auth.store'

export const authApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AuthTokenDto, SignInDtoV3>({
      query: (queryArgs) => ({
        url: '/v3/auth/login',
        method: 'POST',
        data: {
          email: queryArgs.email.trim(),
          password: queryArgs.password,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setAuthInfo({
              userId: data.userId,
              access_token: data.access_token,
              refresh_token: data.refresh_token,
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
    validateMagicToken: builder.mutation<IOrganizationChangeResponse, string>({
      query: (token) => ({
        url: 'v3/auth/magic-link/login',
        method: 'POST',
        data: {
          magicLinkToken: token,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            setAuthInfo({
              userId: '',
              access_token: data.access_token,
              refresh_token: '',
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
    forgotPassword: builder.mutation<void, UserEmailDto>({
      query: (queryArgs) => ({
        url: '/v3/auth/forgot-password',
        method: 'POST',
        data: {
          email: queryArgs.email.trim(),
        },
      }),
    }),
    setNewPassword: builder.mutation<{ Ok: true }, string>({
      query: (password) => ({
        url: '/v3/auth/set-new-password',
        method: 'POST',
        data: {
          password: password,
        },
      }),
    }),
    getOrganizationsByUserId: builder.query<
      ApiResponseSerializer<OrganizationDTOV2>,
      string
    >({
      query: (userId) => ({
        url: `/v3/auth/organizations/${userId}`,
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          const organizations = data.records
            .filter((org) => org?.id !== FUTURE_BANK)
            .sort((a, b) => {
              if (a.name! < b.name!) return -1
              if (a.name! > b.name!) return 1
              return 0
            })

          dispatch(
            setUserSession({
              organizations: organizations,
            }),
          )
        } catch (error) {
          return
        }
      },
    }),
    changeOrganization: builder.mutation<AuthTokenDto, RefreshTokenDtoV3>({
      query: (data) => ({
        url: '/v3/auth/refresh',
        method: 'POST',
        data: {
          organizationId: data.organizationId,
          membershipId: data.membershipId,
          refreshToken: data.refreshToken,
        },
      }),
    }),
    getAuthProfile: builder.query<
      IUserSessionStore & { organization: OrganizationDTOV2 },
      null
    >({
      query: () => ({
        url: '/v3/auth/profile',
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useSetNewPasswordMutation,
  useValidateMagicTokenMutation,
  useGetOrganizationsByUserIdQuery,
  useChangeOrganizationMutation,
  useGetAuthProfileQuery,
  endpoints: authApiEndpoints,
} = authApi
